import * as Types from '@aeppic/types'
import BTreeModule from 'sorted-btree'

export interface IIndex {
  fieldPath: string
  prefixLength: number | null
  isSuperSet: boolean
  size(): number
  entryCount(): number
  addIfMatches(entry: Types.Document): void
  has(id: string): boolean
  remove(id: string): void
  getDocumentIdsForValue(value: any): DocumentIdIterator
  getEntriesForRange(minValue: any, maxValue: any): Array<[key: any, entries: DocumentIdIterator]>
}

/**
 * A thin wrapper around a Set or BTree of DocumentIds that provides iteration capabilities.
 * For Set-backed iterators, sorts keys once during construction for efficiency.
 */
export class DocumentIdIterator implements Iterable<Types.DocumentId> {
  private _sortedKeys?: Types.DocumentId[]
  private _minKey?: Types.DocumentId
  private _maxKey?: Types.DocumentId

  constructor(
    private _collection: Set<Types.DocumentId> | BTreeModule<Types.DocumentId, null>,
    private _isSorted: boolean = false
  ) {
    // For Set-backed iterators, sort once during construction
    if (!_isSorted) {
      this._sortedKeys = Array.from(_collection.keys()).sort()
      if (this._sortedKeys.length > 0) {
        this._minKey = this._sortedKeys[0]
        this._maxKey = this._sortedKeys[this._sortedKeys.length - 1]
      }
    }
  }

  [Symbol.iterator](): Iterator<Types.DocumentId> {
    if (this._isSorted) {
      // BTree keys are already sorted
      return this._collection.keys()
    } else {
      // Return pre-sorted keys for Set
      return this._sortedKeys![Symbol.iterator]()
    }
  }

  get size(): number {
    return this._collection.size
  }

  has(id: Types.DocumentId): boolean {
    return this._collection.has(id)
  }

  /**
   * Returns an iterator over document IDs, optionally starting from a specific key.
   * 
   * @param fromKey Optional starting key. If provided, only returns keys >= fromKey.
   * @returns An iterable iterator of document IDs in sorted order.
   */
  keys(fromKey?: Types.DocumentId): IterableIterator<Types.DocumentId> {
    if (this._isSorted) {
      // BTree supports keys(from) natively for efficient seeking
      if (fromKey !== undefined) {
        return (this._collection as any).keys(fromKey)
      }
      return this._collection.keys()
    } else {
      // For Set: use pre-sorted array
      if (fromKey === undefined) {
        return this._sortedKeys![Symbol.iterator]()
      }
      
      // Binary search to find the first index >= fromKey
      const sortedKeys = this._sortedKeys!
      let left = 0
      let right = sortedKeys.length
      
      while (left < right) {
        const mid = Math.floor((left + right) / 2)
        if (sortedKeys[mid] < fromKey) {
          left = mid + 1
        } else {
          right = mid
        }
      }
      
      // Return iterator starting from the found position
      return sortedKeys.slice(left)[Symbol.iterator]()
    }
  }

  values(): IterableIterator<Types.DocumentId> {
    return this.keys()
  }

  /**
   * Returns the minimum (first) key in sorted order.
   * O(1) for both BTree and Set (cached during construction).
   */
  minKey(): Types.DocumentId | undefined {
    if (this._collection.size === 0) return undefined
    
    if (this._isSorted) {
      // BTree has efficient minKey()
      return (this._collection as any).minKey() as Types.DocumentId
    } else {
      // Pre-computed during construction for Set
      return this._minKey
    }
  }

  /**
   * Returns the maximum (last) key in sorted order.
   * O(1) for both BTree and Set (cached during construction).
   */
  maxKey(): Types.DocumentId | undefined {
    if (this._collection.size === 0) return undefined
    
    if (this._isSorted) {
      // BTree has efficient maxKey()
      return (this._collection as any).maxKey() as Types.DocumentId
    } else {
      // Pre-computed during construction for Set
      return this._maxKey
    }
  }

  get isSorted(): boolean {
    return this._isSorted
  }
}